/**
 * Какой формат уходит клиенту, когда голосовое отправляют «Голосом».
 *
 * Выбор (так делает и панель Chatwoot, AudioRecorder.vue + audioConversionUtils.js):
 *  • Telegram и WhatsApp принимают голосовое как OGG/Opus (WhatsApp Cloud требует именно audio/ogg);
 *  • Android/Chrome пишет WebM/Opus → перепаковка в OGG без потери качества (webmToOgg.ts);
 *  • iPhone/Safari пишет только MP4/AAC, а OGG там получить нельзя → MP3 (его принимают все каналы
 *    как обычное аудио; голосовым «пузырём» в Telegram оно не станет — так же ведёт себя панель Chatwoot);
 *  • если превращение не удалось — отправляем запись как есть (Chatwoot принимает любое audio/*),
 *    чем терять голосовое.
 *
 * С 11.10 наш Chatwoot сам приводит голосовое к формату канала (ffmpeg, Unia::Voice::Normalizer:
 * OGG/Opus для Telegram и WhatsApp — настоящим голосовым пузырём, M4A для Messenger/Instagram, MP3 для
 * сайта). Если сервер это умеет (отдал правила канала — serverNormalizes), запись iPhone уходит как есть
 * (M4A): без долгого MP3 на телефоне и без двойного сжатия. WebM с Android по-прежнему перепаковывается
 * в OGG — это без потерь и быстро.
 */
import { remuxWebmToOgg } from './webmToOgg';

export type VoiceFormat = 'ogg' | 'mp3' | 'm4a' | 'raw';

/** Решение по формату — отдельно от самой перекодировки, чтобы проверять без звука. */
export function planVoiceFormat(mime: string, serverNormalizes = false): 'remux-ogg' | 'keep-ogg' | 'keep-m4a' | 'to-mp3' {
  const base = mime.split(';')[0].trim().toLowerCase();
  if (base === 'audio/ogg' || base === 'application/ogg') return 'keep-ogg';
  if (base === 'audio/webm' || base === 'video/webm') return 'remux-ogg';
  if (serverNormalizes && (base === 'audio/mp4' || base === 'audio/aac' || base === 'audio/x-m4a')) return 'keep-m4a';
  return 'to-mp3';
}

export interface PreparedVoice {
  file: File;
  format: VoiceFormat;
}

const stamp = () => new Date().toISOString().replace(/[^0-9]/g, '').slice(0, 14);

/** Звук → MP3 (моно, 64 кбит/с: для голоса достаточно, файл маленький). */
async function toMp3(blob: Blob): Promise<Blob> {
  const AudioCtx: typeof AudioContext | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) throw new Error('Нет декодера звука');
  const ctx = new AudioCtx();
  try {
    const decoded = await ctx.decodeAudioData(await blob.arrayBuffer());
    const n = decoded.length;
    const mono = new Float32Array(n);
    for (let ch = 0; ch < decoded.numberOfChannels; ch += 1) {
      const data = decoded.getChannelData(ch);
      for (let i = 0; i < n; i += 1) mono[i] += data[i] / decoded.numberOfChannels;
    }
    const pcm = new Int16Array(n);
    for (let i = 0; i < n; i += 1) {
      const s = Math.max(-1, Math.min(1, mono[i]));
      pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    // Кодировщик подгружаем только когда он нужен (iPhone): на Android его в память не берём.
    const { Mp3Encoder } = await import('@breezystack/lamejs');
    const enc = new Mp3Encoder(1, decoded.sampleRate, 64);
    const parts: Uint8Array[] = [];
    const frame = 1152;
    for (let off = 0; off < pcm.length; off += frame) {
      const chunk = enc.encodeBuffer(pcm.subarray(off, Math.min(off + frame, pcm.length)));
      if (chunk.length > 0) parts.push(chunk);
    }
    const tail = enc.flush();
    if (tail.length > 0) parts.push(tail);
    return new Blob(parts as BlobPart[], { type: 'audio/mpeg' });
  } finally {
    void ctx.close().catch(() => undefined);
  }
}

export async function prepareVoiceFile(blob: Blob, mime: string, opts: { serverNormalizes?: boolean } = {}): Promise<PreparedVoice> {
  const plan = planVoiceFormat(mime, opts.serverNormalizes);
  try {
    if (plan === 'keep-m4a') {
      return { file: new File([blob], `voice-${stamp()}.m4a`, { type: 'audio/mp4' }), format: 'm4a' };
    }
    if (plan === 'keep-ogg') {
      return { file: new File([blob], `voice-${stamp()}.ogg`, { type: 'audio/ogg' }), format: 'ogg' };
    }
    if (plan === 'remux-ogg') {
      const ogg = await remuxWebmToOgg(blob);
      return { file: new File([ogg], `voice-${stamp()}.ogg`, { type: 'audio/ogg' }), format: 'ogg' };
    }
    const mp3 = await toMp3(blob);
    return { file: new File([mp3], `voice-${stamp()}.mp3`, { type: 'audio/mpeg' }), format: 'mp3' };
  } catch {
    const ext = mime.includes('mp4') || mime.includes('aac') ? 'm4a' : 'webm';
    return { file: new File([blob], `voice-${stamp()}.${ext}`, { type: blob.type || mime }), format: 'raw' };
  }
}
